import React, { useState, useEffect, useMemo } from 'react';
import { Box, useMediaQuery } from '@mui/material';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import {
  arrayMove,
  rectSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import useTrackPageView from '../../hooks/useTrackPageView';
import { trackPageView } from '../../lib/gaEvents';
import { PAGE_TYPE } from '../../constants/gaEnums';
import theme from '../../theme';
import { useSearchParams, useNavigate } from 'react-router-dom';
import AlbumVerticalCard from '../../components/card/AlbumVerticalCard';
import { reviewsApi } from '../../api/reviews';
import { adminApi } from '../../api/admin';
import { useQueryClient } from '@tanstack/react-query';
import { useReviewsAlbums } from '../review/hooks/useReviewsAlbums';
import ProgressBar from '../../components/common/ProgressBar';
import Pagination from '../../components/common/Pagination';
import SelectFilter from '../../components/common/SelectFilter';
import PageLayout from '../../components/layout/PageLayout';
import PageTitle from '../../components/common/PageTitle';
import NoData from '../../components/common/NoData';
import { useUserStore } from '../../stores/userStore';
import BasicButton from '../../components/button/BasicButton';
import TabButton from '../../components/button/TabButton';
import SubTab from '../../components/button/SubTab';
import InFeedAd from '../../components/ad/InFeedAd';
import SEO from '../../components/common/SEO';
import { SEO_DESCRIPTIONS } from '../../constants/seoDescriptions';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../../utils/env';

const REGION_TABS = [
  { label: '전체', key: 'all' },
  { label: '국내', key: 'korean' },
  { label: '해외', key: 'global' },
];

const CATEGORY_TABS = [
  { label: '최신 앨범', key: 'releases' },
  { label: '포스트팝 추천', key: 'recommended' },
];

const TYPE_FILTER_OPTIONS = [
  { label: '앨범', value: 'albums' },
  { label: '싱글', value: 'singles' },
];

const normalizePeriod = (value) => (value === 'today' ? 'week' : value || 'week');

// 5줄마다 배너 (xs: 2열, md: 4열, lg: 5열)
const ITEMS_PER_AD = { xs: 10, md: 20, lg: 25 };

// 10줄마다 페이지네이션 (xs: 2열, md: 4열, lg: 5열)
const ITEMS_PER_PAGE = { xs: 20, md: 40, lg: 50 };

const getItemsPerPage = (isXs, isMd) =>
  isXs ? ITEMS_PER_PAGE.xs : isMd ? ITEMS_PER_PAGE.md : ITEMS_PER_PAGE.lg;

const getItemsPerAd = (isXs, isMd) =>
  isXs ? ITEMS_PER_AD.xs : isMd ? ITEMS_PER_AD.md : ITEMS_PER_AD.lg;

const SortableAlbumItem = ({ item, children }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
  });

  return (
    <Box
      ref={setNodeRef}
      sx={{
        minWidth: 0,
        position: 'relative',
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.65 : 1,
        zIndex: isDragging ? 2 : 0,
      }}
    >
      <Box
        {...attributes}
        {...listeners}
        sx={{
          position: 'absolute',
          top: { xs: 12, lg: 16 },
          left: { xs: 12, lg: 16 },
          zIndex: 11,
          width: { xs: 24, lg: 32 },
          height: { xs: 24, lg: 32 },
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'rgba(255, 255, 255, 0.92)',
          boxShadow: '0 1px 4px rgba(0, 0, 0, 0.18)',
          color: theme.palette.black[4],
          cursor: isDragging ? 'grabbing' : 'grab',
          touchAction: 'none',
        }}
      >
        <DragIndicatorIcon sx={{ fontSize: { xs: 18, lg: 22 } }} />
      </Box>
      {children}
    </Box>
  );
};

const Albums = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [regionTab, setRegionTab] = useState('all');
  const [categoryTab, setCategoryTab] = useState('releases');
  const [subTab, setSubTab] = useState('albums');
  const toAlbumsCategory = (category, region = regionTab) =>
    category === 'releases' ? `${region}_new_releases` : `${region}_recommended`;
  useTrackPageView({
    pageType: PAGE_TYPE.REVIEWS_MAIN,
    reviewsCategory: toAlbumsCategory(categoryTab),
  });
  const [sortBy, setSortBy] = useState('popular');
  const [period, setPeriod] = useState('week');
  const [isEditing, setIsEditing] = useState(false);
  const [albumsToDelete, setAlbumsToDelete] = useState(new Set());
  const [orderedAlbums, setOrderedAlbums] = useState([]);

  const navigate = useNavigate();
  const { isAdmin, user } = useUserStore();
  const queryClient = useQueryClient();

  // breakpoint 감지
  const isXs = useMediaQuery(theme.breakpoints.down('md'));
  const isMd = useMediaQuery(theme.breakpoints.between('md', 'lg'));
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));

  const itemsPerAd = getItemsPerAd(isXs, isMd);
  const isAdminManageableTab = categoryTab === 'releases' || categoryTab === 'recommended';
  const isRecommendedOrderMode =
    categoryTab === 'recommended' &&
    regionTab === 'all' &&
    subTab === 'albums' &&
    sortBy === 'latest';
  const canDragRecommendedAlbums = isEditing && isRecommendedOrderMode;
  const editingRecommendedTargetSize = isEditing && isRecommendedOrderMode ? 200 : null;

  const REGION_TAB_ITEMS = useMemo(
    () => REGION_TABS.map((tab) => ({ id: tab.key, label: tab.label })),
    [],
  );
  const CATEGORY_TAB_ITEMS = useMemo(
    () => CATEGORY_TABS.map((tab) => ({ id: tab.key, label: tab.label })),
    [],
  );
  const sortFilterOptions = useMemo(
    () =>
      categoryTab === 'recommended'
        ? [
            { label: '인기순', value: 'popular' },
            { label: '최신순', value: 'latest' },
            { label: '발매순', value: 'release' },
            { label: '리뷰순', value: 'reviews' },
            { label: '평점순', value: 'rating' },
          ]
        : [
            { label: '인기순', value: 'popular' },
            { label: '발매순', value: 'latest' },
            { label: '리뷰순', value: 'reviews' },
            { label: '평점순', value: 'rating' },
          ],
    [categoryTab],
  );

  const queryRegion = searchParams.get('region') || 'all';
  const queryCategory = searchParams.get('category') || 'releases';
  const queryType = searchParams.get('type') || 'albums';
  const queryDefaultSort = 'popular';
  const querySort = searchParams.get('sort') || queryDefaultSort;
  const queryPeriod = normalizePeriod(searchParams.get('period'));
  const queryPage = parseInt(searchParams.get('page')) || 1;
  const querySize = editingRecommendedTargetSize || getItemsPerPage(isXs, isMd);
  const apiRegion = queryRegion === 'all' ? null : queryRegion === 'global' ? 'global' : 'kr';

  const { data: reviewsData, isPending: loading } = useReviewsAlbums({
    region: apiRegion,
    category: queryCategory,
    type: queryType,
    sort: querySort,
    page: queryPage,
    size: querySize,
    period: queryPeriod,
  });

  const albums = useMemo(() => reviewsData?.albums ?? [], [reviewsData?.albums]);
  const pagination = reviewsData?.pagination ?? { currentPage: 1, totalPages: 1, totalItems: 0 };
  const visibleAlbums = canDragRecommendedAlbums ? orderedAlbums : albums;

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 160,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  useEffect(() => {
    const region = searchParams.get('region') || 'all';
    const category = searchParams.get('category') || 'releases';
    const type = searchParams.get('type') || 'albums';
    const defaultSort = 'popular';
    const sort = searchParams.get('sort') || defaultSort;
    const periodParam = normalizePeriod(searchParams.get('period'));

    setRegionTab(region);
    setCategoryTab(category);
    setSubTab(type);
    setSortBy(sort);
    setPeriod(periodParam);
  }, [searchParams]);

  useEffect(() => {
    if (!isEditing || canDragRecommendedAlbums) {
      setOrderedAlbums(albums);
    }
  }, [albums, canDragRecommendedAlbums, isEditing]);

  const buildParams = ({
    region = regionTab,
    category = categoryTab,
    type = subTab,
    sort,
    page = '1',
    periodValue = period,
  }) => {
    const defaultSort = 'popular';
    const params = {
      region,
      category,
      type,
      sort: sort || defaultSort,
      page,
    };
    if (category === 'releases') params.period = periodValue;
    return params;
  };

  const handleRegionChange = (newRegion) => {
    trackPageView(
      {
        pageType: PAGE_TYPE.REVIEWS_MAIN,
        reviewsCategory: toAlbumsCategory(categoryTab, newRegion),
      },
      user,
    );
    setIsEditing(false);
    setAlbumsToDelete(new Set());
    setSearchParams(buildParams({ region: newRegion, category: categoryTab, sort: sortBy }));
  };

  const handleCategoryTabChange = (newCategory) => {
    trackPageView(
      { pageType: PAGE_TYPE.REVIEWS_MAIN, reviewsCategory: toAlbumsCategory(newCategory) },
      user,
    );
    setIsEditing(false);
    setAlbumsToDelete(new Set());
    setSearchParams(buildParams({ category: newCategory, type: 'albums' }));
  };

  const handleSubTabChange = (subTab) => {
    setSearchParams(buildParams({ type: subTab, sort: sortBy }));
  };

  const handleSortChange = (newSort) => {
    setSearchParams(buildParams({ sort: newSort }));
  };

  const handlePeriodChange = (newPeriod) => {
    setSearchParams(buildParams({ sort: sortBy, periodValue: newPeriod }));
  };

  const handlePageChange = (event, newPage) => {
    setSearchParams(buildParams({ sort: sortBy, page: newPage.toString() }));
  };

  const handleUploadClick = () => {
    const params = new URLSearchParams({
      region: regionTab,
      category: categoryTab,
      type: subTab,
      sort: sortBy,
      page: '1',
    });
    if (categoryTab === 'releases') {
      params.set('period', period);
    }
    navigate(`/albums/upload?${params.toString()}`);
  };

  const handleEditClick = () => {
    if (categoryTab === 'recommended' && !isRecommendedOrderMode) {
      setSearchParams(
        buildParams({
          region: 'all',
          category: 'recommended',
          type: 'albums',
          sort: 'latest',
          page: '1',
        }),
      );
    }
    setOrderedAlbums(albums);
    setIsEditing(true);
  };

  const handleSaveClick = async () => {
    try {
      if (canDragRecommendedAlbums) {
        const orderedAlbumIds = orderedAlbums
          .map((album) => album.id)
          .filter((albumId) => !albumsToDelete.has(albumId));
        await adminApi.updateRecommendedAlbumOrder(orderedAlbumIds);
      }

      if (albumsToDelete.size > 0) {
        const albumIdsToExclude = Array.from(albumsToDelete);
        if (categoryTab === 'recommended') {
          await reviewsApi.excludeRecommends({ ids: albumIdsToExclude });
        } else {
          await reviewsApi.excludeNewRelease({ ids: albumIdsToExclude });
        }
      }

      setAlbumsToDelete(new Set());
      setIsEditing(false);
      queryClient.invalidateQueries({ queryKey: ['reviewsAlbums'] });
      queryClient.invalidateQueries({ queryKey: ['home'] });
      queryClient.invalidateQueries({ queryKey: ['homeAlbums'] });
    } catch (error) {
      console.error('Failed to save album changes:', error);
    }
  };

  const handleCancelClick = () => {
    setIsEditing(false);
    setAlbumsToDelete(new Set());
    setOrderedAlbums(albums);
  };

  const handleMarkForDeletion = (albumId) => {
    setAlbumsToDelete((prev) => new Set([...prev, albumId]));
  };

  const handleUndoDeletion = (albumId) => {
    setAlbumsToDelete((prev) => {
      const newSet = new Set(prev);
      newSet.delete(albumId);
      return newSet;
    });
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    setOrderedAlbums((items) => {
      const oldIndex = items.findIndex((item) => item.id === active.id);
      const newIndex = items.findIndex((item) => item.id === over.id);
      if (oldIndex < 0 || newIndex < 0) return items;
      return arrayMove(items, oldIndex, newIndex);
    });
  };

  const renderAlbumCard = (item, index) => (
    <AlbumVerticalCard
      albumId={item.id}
      sourceId={item.id}
      referrerSection={toAlbumsCategory(categoryTab)}
      albumSource={'local'}
      slug={item.slug}
      imageUrl={item.imageUrl}
      albumName={item.title}
      artistId={item.artist.sourceId}
      artistDbId={item.artist.id}
      artistSource={item.artist.artistSource}
      artistName={item.artist.name}
      artistSlug={item.artist.slug}
      albumAvgRating={item.averageRating}
      albumGenres={item.genres || item.genreNames || item.albumGenres || []}
      showGenres
      hideBookmarkCount
      commentCount={item.reviewCount}
      bookmarkCount={item.bookmarkCount}
      releaseDate={item.releaseDate}
      contentRating={item.contentRating}
      isRecommended={item.isRecommended}
      isStarred={item.isStarred}
      isEditMode={isEditing}
      isMarkedForDeletion={albumsToDelete.has(item.id)}
      onMarkForDeletion={handleMarkForDeletion}
      onUndoDeletion={handleUndoDeletion}
      ratingSolo={!isLg}
      dateSolo={isLg}
    />
  );

  const renderAlbumGrid = (items) => (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: 'repeat(2, minmax(0, 1fr))',
          md: 'repeat(4, minmax(0, 1fr))',
          lg: 'repeat(5, minmax(0, 1fr))',
        },
        columnGap: { xs: 2, lg: 2.5 },
        rowGap: { xs: 3, md: 5 },
        mt: { xs: 2, md: 2.5, lg: 3.75 },
      }}
    >
      {items.flatMap((item, index) => {
        const card = canDragRecommendedAlbums ? (
          <SortableAlbumItem key={item.id} item={item}>
            {renderAlbumCard(item, index)}
          </SortableAlbumItem>
        ) : (
          <Box key={`${item.id}-${index}`} sx={{ minWidth: 0 }}>
            {renderAlbumCard(item, index)}
          </Box>
        );

        if (
          !canDragRecommendedAlbums &&
          (index + 1) % itemsPerAd === 0 &&
          index < items.length - 1
        ) {
          return [
            card,
            <Box key={`ad-${index}`} sx={{ gridColumn: '1 / -1' }}>
              <InFeedAd pageType={PAGE_TYPE.REVIEWS_MAIN} />
            </Box>,
          ];
        }
        return card;
      })}
    </Box>
  );

  return (
    <PageLayout>
      <SEO title="Albums" description={SEO_DESCRIPTIONS.REVIEWS} url={`${SITE_URL}/albums`} />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Postpop', item: SITE_URL },
              { '@type': 'ListItem', position: 2, name: 'Albums' },
            ],
          })}
        </script>
      </Helmet>
      {albums.length > 0 && (
        <Helmet>
          <script type="application/ld+json">
            {JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              itemListElement: albums
                .filter((item) => item.imageUrl || item.albumImageUrl)
                .map((item, index) => ({
                  '@type': 'ListItem',
                  name: item.title || item.albumTitle,
                  image: item.imageUrl || item.albumImageUrl,
                  url: item.slug
                    ? `${SITE_URL}/reviews/${item.slug}`
                    : `${SITE_URL}/reviews/local/${item.id || item.albumId}`,
                  position: index + 1,
                })),
            })}
          </script>
        </Helmet>
      )}
      {/* 페이지 타이틀 */}
      <PageTitle textAlign={{ xs: 'left', md: 'center' }} mb={{ xs: 2, md: 3.75, lg: 5 }}>
        앨범
      </PageTitle>

      <TabButton
        tabs={CATEGORY_TAB_ITEMS}
        activeTab={categoryTab}
        onTabChange={handleCategoryTabChange}
        marginTop={0}
        centered={!isXs}
        sx={{
          '& .MuiTabs-flexContainer': {
            gap: { xs: 1, md: 1.5 },
          },
          '& .MuiTab-root': {
            minWidth: { xs: 136, md: 210 },
            py: { xs: 1.5, md: 1.75 },
            fontSize: { xs: 14, md: 18 },
          },
        }}
      >
        {/* 버튼 영역 (admin only) */}
        {isAdmin && isAdminManageableTab && (
          <Box sx={{ display: 'flex', gap: 1, mt: { xs: 3.75, lg: 2 } }}>
            {!isEditing && (
              <BasicButton
                variant="outlined"
                onClick={handleUploadClick}
                sx={{ padding: '12px 16px', typography: 'body3', fontWeight: 'medium' }}
              >
                업로드
              </BasicButton>
            )}
            {isEditing ? (
              <>
                <BasicButton
                  variant="outlined"
                  onClick={handleSaveClick}
                  sx={{ padding: '12px 16px', typography: 'body3', fontWeight: 'medium' }}
                >
                  저장 {albumsToDelete.size > 0 && `(${albumsToDelete.size})`}
                </BasicButton>
                <BasicButton
                  variant="outlined"
                  onClick={handleCancelClick}
                  sx={{ padding: '12px 16px', typography: 'body3', fontWeight: 'medium' }}
                >
                  취소
                </BasicButton>
              </>
            ) : (
              <BasicButton
                variant="outlined"
                onClick={handleEditClick}
                sx={{ padding: '12px 16px', typography: 'body3', fontWeight: 'medium' }}
              >
                수정
              </BasicButton>
            )}
          </Box>
        )}

        {/* 서브탭 + 필터 영역 (수정 모드에서는 숨김) */}
        {!isEditing && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: { xs: 2, md: 3 },
              mt: isAdmin && isAdminManageableTab ? { xs: 2, md: 3.75 } : { xs: 2.5, md: 3.75 },
            }}
          >
            <SubTab
              tabs={REGION_TAB_ITEMS}
              activeTab={regionTab}
              onTabChange={handleRegionChange}
              sx={{
                flexShrink: 0,
                gap: { xs: 1.5, lg: 2 },
                maxWidth: '100%',
                '& > div': {
                  px: { xs: 0.25, md: 0.5 },
                },
              }}
            />

            {/* 필터 영역 */}
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'nowrap',
                flexShrink: 0,
                justifyContent: 'flex-end',
                gap: { xs: 1.5, md: 2 },
                rowGap: { xs: 1, md: 1.5 },
                transform: { xs: 'translateY(-5px)', md: 'translateY(-1px)' },
              }}
            >
              <SelectFilter
                label="유형"
                value={subTab}
                onChange={handleSubTabChange}
                options={TYPE_FILTER_OPTIONS}
                hideLabelOnMobile
                valueIconGap={{ xs: 0.25, md: 0.5 }}
              />
              {categoryTab === 'releases' && (
                <SelectFilter
                  label="기간"
                  value={period}
                  onChange={handlePeriodChange}
                  options={[
                    { label: '일주일', value: 'week' },
                    { label: '한달', value: 'month' },
                  ]}
                  hideLabelOnMobile
                  valueIconGap={{ xs: 0.25, md: 0.5 }}
                />
              )}
              <SelectFilter
                label="정렬"
                value={sortBy}
                onChange={handleSortChange}
                options={sortFilterOptions}
                hideLabelOnMobile
                valueIconGap={{ xs: 0.25, md: 0.5 }}
              />
            </Box>
          </Box>
        )}

        <ProgressBar isLoading={loading}>
          {visibleAlbums.length === 0 ? (
            <NoData />
          ) : (
            <>
              {canDragRecommendedAlbums ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  modifiers={[restrictToParentElement]}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={visibleAlbums.map((item) => item.id)}
                    strategy={rectSortingStrategy}
                  >
                    {renderAlbumGrid(visibleAlbums)}
                  </SortableContext>
                </DndContext>
              ) : (
                renderAlbumGrid(visibleAlbums)
              )}
              {!canDragRecommendedAlbums && (
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePageChange}
                />
              )}
            </>
          )}
        </ProgressBar>
      </TabButton>
    </PageLayout>
  );
};

export default Albums;
